import { Button, Flex, Spinner } from "src/elements";
import { withStudioWizard } from "src/pages/CdtStudio/store/atoms";
import { StoresSelector } from "src/components";
import { useEffect, useMemo, useRef, useState } from "react";
import { StoresSelectorStore } from "src/components/StoresSelector/store/types";
import {
  ResponseGetMerchflowsFilters,
  RouteGetMerchflowsFilters,
} from "@CommonApi/merchflows/filters";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { useMerchflowsApi } from "src/api/merchflows";
import { RoutePutMerchflowsFilterConfig_filterConfigId_ } from "@CommonApi/merchflows/filter-config/_filterConfigId_";
import { ResponsePostMerchflowsUhd } from "@CommonApi/merchflows/uhd";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { routeToUrl } from "@CommonUtils/routes";
import { mapFilterStoreToStoreConfig } from "src/utils/storeConfig";
import { useTranslation } from "src/i18n";
import type { CdtVersionContext } from "src/pages/CDTTreeEditor/store/cdtVersionContextAtom";
import { useCdtCategoryStoresQuery } from "src/pages/CDTTreeEditor/store/useCdtCategoryStoresQuery";
import { StoreCdtVersion, StoresPickList } from "./StoresPickList";

interface Props {
  uhdFlowResponse: ResponsePostMerchflowsUhd;
  availableFilters: ResponseGetMerchflowsFilters | null;
  /**
   * "Next → Generate" from a CDT version: its stores are ticked when the step
   * opens and every row shows the version it uses.
   */
  cdtVersionContext?: CdtVersionContext | null;
  /** The stores are saved and the flow moves on to its base planogram. */
  onProceed?: () => void;
}

export const Step2StoresUhd = ({
  uhdFlowResponse,
  availableFilters,
  cdtVersionContext,
  onProceed,
}: Props) => {
  const { hasLoader } = useLoaders();
  const { putMerchflowsFilterConfig_filterConfigIdApi } = useMerchflowsApi();
  const { closeModal } = useModals();
  const { navigate } = useNavigation();
  const { t } = useTranslation("common");
  const initialStoreCodes = cdtVersionContext?.storeCodes;
  const categoryStoresQuery = useCdtCategoryStoresQuery(cdtVersionContext?.categoryId);
  // Which version each store resolves to, for the CDT version column.
  const cdtVersions = useMemo(() => {
    if (!cdtVersionContext || !categoryStoresQuery.data) return undefined;
    const byCode = new Map<string, StoreCdtVersion>();
    for (const store of categoryStoresQuery.data) {
      if (store.effectiveTreeId === null) continue;
      byCode.set(store.storeCode, {
        treeId: store.effectiveTreeId,
        label: `#${store.effectiveTreeId} ${store.effectiveTreeName ?? ""}`.trim(),
        isPinned: !store.isDefaultFallback,
      });
    }
    return byCode;
  }, [cdtVersionContext, categoryStoresQuery.data]);

  const isFiltersLoading = hasLoader(RouteGetMerchflowsFilters);
  // Stores matching the attribute pre-filter (bay size / business / activity type).
  const [pool, setPool] = useState<StoresSelectorStore[]>([]);
  // Store codes the user has explicitly hand-picked from within the pool.
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const hasAppliedInitialCodes = useRef(false);

  // Keep manual picks consistent with the attribute filter: a store only counts
  // if it also passes the current filters, so drop any pick that left the pool.
  // The first time the pool fills, the preselection from a CDT version is applied.
  useEffect(() => {
    const poolCodes = new Set(pool.map((s) => s.store));
    if (!hasAppliedInitialCodes.current && pool.length > 0 && initialStoreCodes?.length) {
      hasAppliedInitialCodes.current = true;
      setSelectedCodes(new Set(initialStoreCodes.filter((code) => poolCodes.has(code))));
      return;
    }
    setSelectedCodes((prev) => {
      const next = new Set([...prev].filter((code) => poolCodes.has(code)));
      return next.size === prev.size ? prev : next;
    });
  }, [pool, initialStoreCodes]);

  if (isFiltersLoading) {
    return (
      <Flex flexGrow>
        <Spinner size="big" label={t("storesPickList.loadingStores")} isCentered />
      </Flex>
    );
  }

  const saveFilterConfigAndNavigate = async () => {
    if (!availableFilters || selectedCodes.size === 0) return;

    const stores = availableFilters.stores
      .filter((s) => selectedCodes.has(s.store))
      .map(mapFilterStoreToStoreConfig);

    const result = await putMerchflowsFilterConfig_filterConfigIdApi(
      uhdFlowResponse.filter_config_id,
      {
        filter_config: {
          stores,
          baySizes: [],
          clusters: [],
          states: [],
          cdt: {},
          hierarchy: {},
          fixtures: { section_height: [], section_width: [] },
        },
      },
    );

    if (!result) return;

    onProceed?.();
    navigate(
      withStudioWizard(
        routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
          merchflowId: uhdFlowResponse.id,
          basePogId: uhdFlowResponse.base_pog_id,
        }),
      ),
    );
    closeModal("ModalMerchflowReview");
  };

  return (
    <Flex column flexGrow gap="20px">
      {availableFilters && (
        <>
          <StoresSelector
            availableStores={availableFilters.stores.map((s) => ({
              store: s.store,
              bay_size: s.baySize,
              business_type: s.businessType,
              activity_type: s.activityType,
            }))}
            selectedStores={pool}
            setSelectedStores={setPool}
          />

          <StoresPickList
            stores={pool}
            selectedCodes={selectedCodes}
            setSelectedCodes={setSelectedCodes}
            cdtVersions={cdtVersions}
          />
        </>
      )}

      <Flex gap="10px" justify="right">
        <Button
          isDisabled={selectedCodes.size === 0}
          isLoading={hasLoader(RoutePutMerchflowsFilterConfig_filterConfigId_)}
          onClick={saveFilterConfigAndNavigate}
        >
          {t("storesPickList.nextButton", {
            count: String(selectedCodes.size),
            label:
              selectedCodes.size === 1
                ? t("storesPickList.storeLabel")
                : t("storesPickList.storesLabel"),
          })}
        </Button>
      </Flex>
    </Flex>
  );
};
